import React, { useEffect, useRef, useState } from "react";
import { Col, Form, Row, Table, Dropdown, Pagination } from "react-bootstrap";
import { Common, EmployeeIcons, SettingsIcons } from "../../assets/images";
import { useNavigate } from "react-router-dom";
// Redux Imports
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  setShowOfferLetterExportDropdown,
  setOfferLetterPageSize,
  setOfferLetterPageNo,
  setFilterOfferLetterPageNo,
  setFilterOfferLetterPageSize,
} from "../../store/slices/lettersSlice";
import { setOfferLetterChange } from "../../store/slices/commonSlice";
import { LoaderBlue } from "../Lotties";
// API Imports
import { useDeleteOfferLetterMutation } from "../../services/Letters";
import { OfferLetter } from "../../interfaces/letters";
import { exportToCsv, exportToPDF } from "../../config";
import InterviewedEmployeesFilterModal, {
  FormProps,
} from "../Modals/interviewed-filter";
import Content from "./get-all-candidates-api";
import { useReactToPrint } from "react-to-print";

const AllInterviewers = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const tableRef = useRef(null);
  const [print, setPrint] = useState(false);
  const handlePrint = useReactToPrint({
    content: () => tableRef.current,
    onAfterPrint: () => setPrint(false),
  });
  const handlePrinted = async () => {
    await setPrint(true);
    handlePrint();
  };
  const state = useAppSelector((state) => state.letters);
  const accessToken = useAppSelector((state) => state.common.accessToken);
  const userID = useAppSelector((state) => state.common.userID);
  const profileData = useAppSelector((state) => state.settings.profileData);
  const [deleteLoadingMap, setDeleteLoadingMap] = useState<{
    [key: string]: boolean;
  }>({});
  const offerLetterChange = useAppSelector(
    (state) => state.common.offerLetterChange,
  );

  // Pagination && Filter
  const [filterInput, setFilterInput] = useState<FormProps>({
    hiringStatus: "",
    department: "",
    fromDate: "",
    interviewConducted: "",
    toDate: "",
    role: "",
    nationality: "",
  });
  const [filterSearch, setFilterSearch] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [filteredData, setFilteredData] = useState<OfferLetter[]>([]);
  const totalPages = Math.ceil(
    state.offerLetterTotalPages / state.offerLetterPageSize,
  );
  const totalCount = filterSearch
    ? state.filterOfferLetterTotalPages
    : state.offerLetterTotalPages;

  const filterTotalPages = Math.ceil(
    state.filterOfferLetterTotalPages / state.filterOfferLetterPageSize,
  );

  // show 5 pages at a time in pagination bar and rest will be shown in next and previous
  const showPages = 5;
  const startPage =
    Math.floor((state.offerLetterPageNo - 1) / showPages) * showPages;
  const endPage = startPage + showPages;
  const pages = [...Array(totalPages).keys()].slice(startPage, endPage);
  const filterStartPage =
    Math.floor((state.filterOfferLetterPageNo - 1) / showPages) * showPages;
  const filterEndPage = filterStartPage + showPages;
  const filterPages = [...Array(filterTotalPages).keys()].slice(
    filterStartPage,
    filterEndPage,
  );
  // handle go to page number that is after the 4 pages of current page
  const handleGoToNextPages = () => {
    //   if it is in the last 5 pages then dont do anything
    if (state.offerLetterPageNo > totalPages - showPages) {
      return;
    }
    dispatch(setOfferLetterPageNo(state.offerLetterPageNo + showPages));
  };

  const handleGoToPreviousPages = () => {
    //   if it is in the first 5 pages then dont do anything
    if (state.offerLetterPageNo <= 5) {
      return;
    }
    dispatch(setOfferLetterPageNo(state.offerLetterPageNo - showPages));
  };

  const handleFilterGoToNextPages = () => {
    //   if it is in the last 5 pages then dont do anything
    if (state.filterOfferLetterPageNo > filterTotalPages - showPages) {
      return;
    }
    dispatch(
      setFilterOfferLetterPageNo(state.filterOfferLetterPageNo + showPages),
    );
  };

  const handleFilterGoToPreviousPages = () => {
    //   if it is in the first 5 pages then dont do anything
    if (state.filterOfferLetterPageNo <= 5) {
      return;
    }
    dispatch(
      setFilterOfferLetterPageNo(state.filterOfferLetterPageNo - showPages),
    );
  };

  const handlePageChange = (page: any) => {
    dispatch(setOfferLetterPageNo(page));
  };

  const handleFilterPageChange = (page: any) => {
    dispatch(setFilterOfferLetterPageNo(page));
  };

  const handlePageSizeChange = (event: any) => {
    const selectedPageSize = parseInt(event.target.value, 10); // Parse the value to a number
    if (filterSearch) {
      dispatch(setFilterOfferLetterPageSize(selectedPageSize));
    } else {
      dispatch(setOfferLetterPageSize(selectedPageSize));
    }
  };
  useEffect(() => {
    setFilteredData(state.offerLetter);
  }, [state.offerLetter]);
  // Employees List
  const tableData = {
    tableHeading: [
      "Sr. No.",
      "Req Ref.",
      "Candidate Id.",
      "Candidate Name",
      "Nationality",
      "Department",
      "Role",
      "Interview Conducted",
      "Hiring Status",
      "Offer Letter Status",
      "Actions",
    ],
    tableData: filteredData,
  };

  const csvData = tableData.tableData.map((item, index) => ({
    "Sr. No.": index + 1,
    "Req Ref.": item.reqRef,
    "Candidate Id.": item.intervieweeId,
    "Candidate Name": item.intervieweeName,
    Nationality: item.nationality,
    Department: item.department,
    Role: item.role,
    "Interview Conducted": item.interviewConducted ? "Yes" : "No",
    "Hiring Status": item.interviewerApprovalsStatus,
    "Offer Letter Status": item.offerLetterStatus,
  }));

  const [deleteOfferLetter] = useDeleteOfferLetterMutation();

  const handleDelete = async (empId: string) => {
    setDeleteLoadingMap((prevMap) => ({ ...prevMap, [empId]: true }));
    try {
      const data = {
        userID: userID || "",
        accessToken: accessToken || "",
        permission: profileData?.permissions.deleteEmployee || false,
        payload: {
          intervieweeId: empId,
        },
      };
      await deleteOfferLetter(data).unwrap();
      dispatch(setOfferLetterChange(!offerLetterChange));
    } catch (e) {
      console.log(e);
    } finally {
      setDeleteLoadingMap((prevMap) => ({ ...prevMap, [empId]: false }));
    }
  };

  const handleReset = () => {
    setQuery("");
    setSearch("");
    setFilteredData(state.offerLetter);
    setFilterSearch(false);
    setFilterInput({
      hiringStatus: "",
      department: "",
      fromDate: "",
      interviewConducted: "",
      toDate: "",
      role: "",
      nationality: "",
    });
  };

  const twoWords = (name: string) => {
    const words = name.split(" ");
    return words[0] + " " + words[1];
  };

  return (
    <div className="user-list mt-2 section px-4 py-4">
      <Content />
      <InterviewedEmployeesFilterModal
        show={showFilterModal}
        setShow={setShowFilterModal}
        setCandidates={setFilteredData}
        filterSearch={filterSearch}
        setFilterSearch={setFilterSearch}
        candidates={state.offerLetter}
        query={query}
        filterInput={filterInput}
        setFilterInput={setFilterInput}
      />
      <Row>
        <Col sm="12">
          <div className="section-heading ">
            <h1 className="heading mb-0">New Candidates</h1>
            <div className="d-flex align-items-center justify-content-between mt-4">
              <div
                className=" d-flex align-items-center gap-2"
                style={{ width: "25%" }}
              >
                <Form.Group className="w-100" controlId="formBasicEmail">
                  <Form.Group className="position-relative input-design d-flex align-items-center">
                    <Form.Control
                      type="text"
                      name="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="custom-input"
                      placeholder="Search by candidate id"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setQuery(search);
                        setFilterSearch(true);
                      }}
                      className="btn p-0 border-0"
                    >
                      <img src={Common.Search} alt="search" />
                    </button>
                  </Form.Group>
                </Form.Group>
                {query !== "" ? (
                  <button
                    type="button"
                    className="btn primary-button w-25"
                    onClick={handleReset}
                  >
                    {" "}
                    Reset
                  </button>
                ) : filterSearch ? (
                  <button
                    type="button"
                    className="btn primary-button w-25"
                    onClick={handleReset}
                  >
                    {" "}
                    Reset
                  </button>
                ) : null}
              </div>
              <div className="d-flex align-items-center gap-4">
                <button
                  className="dropdown-toggle-white dropdown-toggle-blue"
                  type="button"
                  onClick={() =>
                    navigate("/initial-evaluation/add-interviewer")
                  }
                >
                  <img src={Common.Plus} height="22px" width="22px" alt="img" />{" "}
                  New
                </button>
                <button
                  id="dropdown-basic"
                  onClick={() => {
                    setShowFilterModal(true);
                  }}
                  className="dropdown-toggle-white dropdown-toggle-blue"
                >
                  Filter{" "}
                </button>
                <Dropdown
                  show={state.showOfferLetterExportDropdown}
                  className="position-relative"
                >
                  <button
                    id="dropdown-basic"
                    onClick={() => {
                      dispatch(
                        setShowOfferLetterExportDropdown(
                          !state.showOfferLetterExportDropdown,
                        ),
                      );
                    }}
                    className=" bg-transparent dropdown-toggle-white"
                  >
                    Export{" "}
                    <img
                      src={EmployeeIcons.ArrowDownBlue}
                      className="ms-2"
                      alt="down-arrow"
                    />
                  </button>
                  <Dropdown.Menu
                    className="mt-1 custom-scrollbar"
                    show={state.showOfferLetterExportDropdown}
                  >
                    <Dropdown.Item
                      onClick={() => {
                        exportToCsv(csvData, "new-candidates-list");
                        dispatch(setShowOfferLetterExportDropdown(false));
                      }}
                    >
                      CSV
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        dispatch(setShowOfferLetterExportDropdown(false));
                        handlePrinted();
                      }}
                    >
                      PDF
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          </div>
        </Col>
        <Col sm="12">
          {state.offerLetterLoading ? (
            <LoaderBlue height={70} width={70} />
          ) : profileData.permissions.showEmployee ? (
            <>
              {tableData.tableData?.length > 0 ? (
                <div className="p-3" ref={tableRef}>
                  {print && (
                    <>
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <img src={Common.Vision} width="110px" alt="logo" />
                        </div>
                        <div className="letter-logo text-center gap-2">
                          <img src={Common.LogoSVG} width="80px" alt="logo" />
                          <div className="mt-1">
                            <h3
                              className="logo-text mb-0"
                              style={{
                                color: "#202020",
                                fontSize: "10px",
                                fontWeight: "700",
                              }}
                            >
                              Senyar Arabian Trading Co. LTD
                            </h3>
                            <h3
                              className="logo-text logo-arabic mb-0"
                              style={{
                                color: "#202020",
                                fontSize: "13px",
                                fontWeight: "700",
                              }}
                            >
                              شركة سنيار العربية التجارية المحدودة
                            </h3>
                          </div>
                        </div>
                      </div>
                      <h1 className="sub-heading text-center mt-3">
                        New Candidates List
                      </h1>
                    </>
                  )}
                  <Table
                    striped
                    bordered
                    responsive
                    className={
                      print
                        ? "mt-5 text-center print-table"
                        : "mt-5 text-center"
                    }
                  >
                    <thead>
                      <tr className="border-0">
                        {tableData.tableHeading.map((item, index) => (
                          <th
                            className={
                              item === "Actions" && print
                                ? "border-0 d-none"
                                : "border-0"
                            }
                            key={index}
                          >
                            {item}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.tableData.map((item, index) => (
                        <tr
                          key={index}
                          style={{ borderColor: "rgba(32, 32, 32, 0.20)" }}
                        >
                          <td>
                            {totalCount > 0
                              ? filterSearch
                                ? (state.filterOfferLetterPageNo - 1) *
                                    state.filterOfferLetterPageSize +
                                  index +
                                  1
                                : (state.offerLetterPageNo - 1) *
                                    state.offerLetterPageSize +
                                  index +
                                  1
                              : index + 1}
                          </td>
                          <td>{item.reqRef}</td>
                          <td
                            className="cursor-pointer"
                            onClick={() =>
                              navigate(
                                `/initial-evaluation/view-interviewer/${item.intervieweeId}`,
                                { state: { data: item } },
                              )
                            }
                          >
                            {item.intervieweeId}
                          </td>
                          <td
                            className="cursor-pointer"
                            onClick={() =>
                              navigate(
                                `/initial-evaluation/view-interviewer/${item.intervieweeId}`,
                                { state: { data: item } },
                              )
                            }
                          >
                            {" "}
                            {print
                              ? twoWords(item.intervieweeName)
                              : item.intervieweeName}
                          </td>
                          <td>{item.nationality}</td>
                          <td>{item.department}</td>
                          <td>{item.role}</td>
                          <td>{item.interviewConducted ? "Yes" : "No"}</td>
                          <td>{item.interviewerApprovalsStatus}</td>
                          <td>{item.offerLetterStatus}</td>
                          {!print && (
                            <td>
                              <button
                                className="btn p-0 border-0 me-2"
                                onClick={() =>
                                  navigate(
                                    `/initial-evaluation/view-interviewer/${item.intervieweeId}`,
                                    { state: { data: item } },
                                  )
                                }
                              >
                                <img
                                  src={Common.Eye}
                                  width="30px"
                                  height="30px"
                                  alt="edit-user"
                                />
                              </button>
                              <button
                                className="btn p-0 border-0 me-2"
                                onClick={() =>
                                  navigate(
                                    `/initial-evaluation/edit-interviewer/${item.intervieweeId}`,
                                    { state: { data: item } },
                                  )
                                }
                              >
                                <img
                                  src={SettingsIcons.EditUser}
                                  width="30px"
                                  height="30px"
                                  alt="edit-user"
                                />
                              </button>
                              {deleteLoadingMap[item.intervieweeId] ? (
                                <LoaderBlue height={20} width={20} />
                              ) : (
                                <button
                                  className="btn p-0 border-0"
                                  onClick={() =>
                                    handleDelete(item.intervieweeId)
                                  }
                                >
                                  <img
                                    src={SettingsIcons.Delete}
                                    width="24px"
                                    height="24px"
                                    alt="edit-user"
                                  />
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center mt-5">
                  <h1 className="heading">No new candidates found</h1>
                </div>
              )}
            </>
          ) : (
            <div className="text-center mt-5">
              <h1 className="heading">Unauthorized Access</h1>
            </div>
          )}
        </Col>
        {filteredData.length > 0 && (
          <Col sm="12">
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <Form.Group
                className="d-flex align-items-center gap-2"
                controlId="formBasicEmail"
              >
                <h4
                  className="sub-heading normal-font-weight textBlack"
                  style={{ fontSize: "14px", width: "230px" }}
                >
                  Entries per page
                </h4>
                <Form.Select
                  name="pageSize"
                  onChange={handlePageSizeChange}
                  value={
                    filterSearch
                      ? state.filterOfferLetterPageSize.toString()
                      : state.offerLetterPageSize.toString()
                  }
                  className="custom-input border-fill custom-height"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="100">100</option>
                  <option value="10000">All</option>
                </Form.Select>
              </Form.Group>
              <div className="pagination-custom">
                <Pagination className="justify-content-end">
                  <Pagination.Item
                    onClick={() =>
                      filterSearch
                        ? handleFilterGoToPreviousPages()
                        : handleGoToPreviousPages()
                    }
                  >
                    <img src={Common.DoubleArrowLeft} alt="arrow" />
                  </Pagination.Item>
                  <Pagination.Item
                    onClick={() =>
                      filterSearch
                        ? handleFilterPageChange(
                            state.filterOfferLetterPageNo > 1
                              ? state.filterOfferLetterPageNo - 1
                              : 1,
                          )
                        : handlePageChange(
                            state.offerLetterPageNo > 1
                              ? state.offerLetterPageNo - 1
                              : 1,
                          )
                    }
                  >
                    <img
                      src={EmployeeIcons.ArrowDownBlue}
                      className="rotate-left"
                      alt="arrow"
                    />
                  </Pagination.Item>
                  {filterSearch ? (
                    <>
                      {/* render filter pages here */}
                      {filterPages.map((page, index) => (
                        <Pagination.Item
                          key={index}
                          onClick={() => handleFilterPageChange(page + 1)}
                          className={
                            page + 1 === state.filterOfferLetterPageNo
                              ? "active-page"
                              : ""
                          }
                        >
                          {page + 1}
                        </Pagination.Item>
                      ))}
                    </>
                  ) : (
                    <>
                      {/* render pages here */}
                      {pages.map((page, index) => (
                        <Pagination.Item
                          key={index}
                          onClick={() => handlePageChange(page + 1)}
                          className={
                            page + 1 === state.offerLetterPageNo
                              ? "active-page"
                              : ""
                          }
                        >
                          {page + 1}
                        </Pagination.Item>
                      ))}
                    </>
                  )}
                  <Pagination.Item
                    onClick={() =>
                      filterSearch
                        ? handleFilterPageChange(
                            state.filterOfferLetterPageNo < filterTotalPages
                              ? state.filterOfferLetterPageNo + 1
                              : filterTotalPages,
                          )
                        : handlePageChange(
                            state.offerLetterPageNo < totalPages
                              ? state.offerLetterPageNo + 1
                              : totalPages,
                          )
                    }
                  >
                    <img
                      src={EmployeeIcons.ArrowDownBlue}
                      className="rotate-right"
                      alt="arrow"
                    />
                  </Pagination.Item>
                  <Pagination.Item
                    onClick={() => {
                      filterSearch
                        ? handleFilterGoToNextPages()
                        : handleGoToNextPages();
                    }}
                  >
                    <img src={Common.DoubleArrowRight} alt="arrow" />
                  </Pagination.Item>
                </Pagination>
              </div>
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};
export default AllInterviewers;
